import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import type {IUserStore} from "data/stores/user/user.store";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {OVERRIDE_LANG} from "data/constants";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {ModalType} from "data/enums";
import {filter, find, first, includes, isNull, toLower} from "lodash";
import {Bindings} from "data/constants/bindings";
import {trackSentryErrors} from "data/utils";
import {AxiosError} from "axios";
import {useNavigate, Location} from "react-router-dom";
import {WIDGET_REDIRECT_URL} from "data/constants/widget";
import {IGameInfoFragment} from "data/providers/api/localization.api.provider";
import {createConnextraScriptTag, ConnextraType} from "data/utils/connextra";
import {ShareUtility} from "data/utils/social_share/social_share.utility";
import {reaction} from "mobx";

interface IInit {
	navigate: ReturnType<typeof useNavigate>;
	location: Location<Location>;
}

export interface IGameGateController extends ViewController<IInit> {
	get isSessionChecked(): boolean;
}

@injectable()
export class GameGateController implements IGameGateController {
	// private _userLocale = navigator.language;
	private _userLocale = OVERRIDE_LANG;
	private disposers: ReturnType<typeof reaction>[] = [];
	private _navigate: ReturnType<typeof useNavigate> | null = null;

	constructor(
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.LocalizationStore) private _i18nStore: ILocalizationStore,
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore
	) {}

	get isSessionChecked(): boolean {
		return this._userStore.isSessionChecked;
	}

	dispose(): void {
		this.disposers.forEach((dispose) => dispose());
	}

	private isUserLocaleHasOccurrences = (locale: string) => {
		return includes(toLower(locale), toLower(this._userLocale));
	};

	private findApplicableLocale(locales: string[]) {
		const userCroppedLocale = first(toLower(this._userLocale).split("-"))!;

		const potentialLocale = find(locales, (localeName) =>
			includes(toLower(localeName), userCroppedLocale)
		);

		const commonLocale = find(locales, (localeName) =>
			includes(toLower(localeName), OVERRIDE_LANG)
		);

		return potentialLocale || commonLocale || first(locales)! || this._userLocale;
	}

	private defineLocale(): string {
		// const {locales} = await this._i18nStore.requestGameInfo(GAME_ID);
		//
		// const isApplicationSupportCurrentLocale = some(locales, (locale) =>
		// 	isEqual(toLower(locale), toLower(this._userLocale))
		// );
		//
		// if (!isApplicationSupportCurrentLocale) {
		// 	/**
		// 	 * If browser's locale isn't in the list of available locales
		// 	 * we're trying to find any locale that looks like user's locale, or any with the EN language,
		// 	 * or the first one that is available, otherwise, use the current locale.
		// 	 */
		// 	this._userLocale = this.findApplicableLocale(locales);
		// }

		// this.redirectToSupportedLocale(urls);

		return this._userLocale || "en";
	}

	private _redirectToSupportedLocale(urls: IGameInfoFragment["data"]["game"]["urls"]) {
		const {hostname} = window.location;
		/**
		 * Look for available domain for the current userLocale (excluding the current domain),
		 * otherwise do nothing and keep using the same userLocale and domain.
		 */
		const localeConfig = first(
			filter(urls, ({url, locale}) => {
				const isDifferentDomain = !url.includes(hostname);
				const hasSameLocale = this.isUserLocaleHasOccurrences(locale);

				return isDifferentDomain && hasSameLocale;
			})
		);

		if (localeConfig?.url) {
			/**
			 * Redirect a user to the site with a supported locale
			 */
			const originPathname = window.location.pathname;
			const checkEndSlash = originPathname.endsWith("/")
				? originPathname
				: `${originPathname}/`;
			const pathSplit = checkEndSlash.split("/");
			pathSplit[1] = localeConfig.path;
			const path = pathSplit.join("/") + window.location.search;
			window.location.replace(new URL(path, localeConfig.url));
		}
	}
	private static checkIsValidWidgetParams(...params: Array<string | null | undefined>): boolean {
		return params.every((param) => typeof param === "string");
	}

	async init(param: IInit): Promise<void> {
		const {navigate, location} = param;
		this._navigate = navigate;

		this.trackLoginState(location);
		try {
			const locale = this.defineLocale();

			/**
			 * The locale may be undefined only in case of redirect.
			 * For all other cases the locale will be defined
			 */
			if (!locale) return;

			/**
			 * If all is good, request the translations for the current locale
			 */
			await this._i18nStore.switchLocale({
				locale,
			});
		} catch (err) {
			trackSentryErrors(err, {}, "game gate - init");
			const error = err as AxiosError;
			this._modalsStore.showModal(ModalType.ERROR, {
				message: error.message,
				errors: error.response?.data,
			});
		}

		await this._userStore.restoreSession();
		await this._userStore.requestUserProfile();

		const modalsDisposer = reaction(
			() => ({
				termsChecked: this._userStore.isTermsChecked,
				isAuthorized: this._userStore.isAuthorized,
			}),
			({termsChecked, isAuthorized}) => {
				if (!isAuthorized) {
					return;
				}
				if (isNull(termsChecked)) {
					this._modalsStore.showModal(ModalType.SECOND_REGISTRATION);
					return;
				}
				const hasRedirected = this.redirectFromWidget();
				if (hasRedirected) {
					return;
				}

				this.redirectToLeaguesJoin();
			},
			{
				fireImmediately: true,
			}
		);

		this.disposers.push(modalsDisposer);
	}

	private redirectFromWidget() {
		const params = new URLSearchParams(window.location.search);
		const contestId = window.location.pathname?.split("/").pop();
		const questionId = params.get("questionId");
		const optionId = params.get("optionId");

		let redirectUrl = localStorage.getItem(WIDGET_REDIRECT_URL);

		if (GameGateController.checkIsValidWidgetParams(questionId, optionId, contestId)) {
			redirectUrl = `/contest/${contestId!}?questionId=${questionId!}&optionId=${optionId!}`;
			localStorage.setItem(WIDGET_REDIRECT_URL, redirectUrl);
		}

		if (redirectUrl) {
			// redirect
			localStorage.removeItem(WIDGET_REDIRECT_URL);
			this._navigate?.(redirectUrl);
			return true;
		}

		return false;
	}
	private redirectToLeaguesJoin() {
		const backNavigate = ShareUtility.getBackURL();

		if (backNavigate) {
			ShareUtility.removeBackURL();
			this._navigate?.(backNavigate);
		}
	}

	onChange(param: IInit) {
		this.trackLoginState(param.location);
	}

	trackLoginState = (location: IInit["location"]) => {
		if (location.state) {
			const searchParams = new URLSearchParams(location.state.search);
			if (searchParams.get("callback") === "login") {
				createConnextraScriptTag(ConnextraType.LOGIN);
			}
		}
	};
}
